import React, { useState, useEffect } from 'react';
import { Button, Modal, Form } from 'react-bootstrap';
import '../assets/css/cookie-consent.css';
import '../assets/css/registration-modal.css';

const defaultConsent = {
  necessary: true,
  functional: false,
  performance: false,
  analytics: false,
  advertising: false,
  others: false,
};

const categoryMeta: Record<keyof typeof defaultConsent, { label: string; description: string }> = {
  necessary: {
    label: 'Nécessaires (Requis)',
    description:
      'Cookies essentiels au bon fonctionnement et à la sécurité du site. Ils doivent rester actifs et ne collectent pas de données personnelles.',
  },
  functional: {
    label: 'Fonctionnels',
    description:
      'Destinés à des fonctionnalités facultatives (partage sur les réseaux sociaux, formulaires tiers, etc.).',
  },
  performance: {
    label: 'Performance',
    description: 'Mesurent des indicateurs de performance du site pour optimiser l\'expérience utilisateur.',
  },
  analytics: {
    label: 'Analytics',
    description:
      'Aident à comprendre comment les visiteurs utilisent le site (nombre de visites, taux de rebond, sources de trafic).',
  },
  advertising: {
    label: 'Publicité',
    description: 'Suivent l\'internaute pour proposer des publicités et des campagnes marketing pertinentes.',
  },
  others: {
    label: 'Autres',
    description: 'Regroupent les cookies en cours de classification.',
  },
};

const parseConsent = (value: string | null) => {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    if (parsed && typeof parsed === 'object' && 'necessary' in parsed) {
      return parsed;
    }
  } catch {
    if (value === 'accepted') {
      return {
        ...defaultConsent,
        functional: true,
        performance: true,
        analytics: true,
        advertising: true,
        others: true,
      };
    }
    if (value === 'refused') {
      return defaultConsent;
    }
  }
  return null;
};

const CookieConsent: React.FC = () => {
  const [show, setShow] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [consent, setConsent] = useState(defaultConsent);

  useEffect(() => {
    const cookieConsent = localStorage.getItem('cookieConsent');
    const parsed = parseConsent(cookieConsent);
    if (parsed) {
      setConsent(parsed);
      return;
    }

    const timer = setTimeout(() => {
      setShow(true);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const saveConsent = (newConsent: typeof defaultConsent) => {
    localStorage.setItem('cookieConsent', JSON.stringify(newConsent));
    setConsent(newConsent);
    setShow(false);
  };

  const handleAccept = () => {
    saveConsent({
      necessary: true,
      functional: true,
      performance: true,
      analytics: true,
      advertising: true,
      others: true,
    });
  };

  const handleRefuse = () => saveConsent(defaultConsent);

  const handleToggleCategory = (category: keyof typeof defaultConsent) => {
    if (category === 'necessary') return;
    setConsent((prev) => ({ ...prev, [category]: !prev[category] }));
  };

  const handleSavePreferences = () => {
    saveConsent(consent);
    setShowPreferences(false);
  };

  if (!show) {
    return null;
  }

  return (
    <>
      <div className="cookie-consent-bar" role="dialog" aria-live="polite">
        <div className="cookie-consent-bar-content">
          <div className="cookie-consent-info cookie-consent-info-text">
            <strong>Gestion des cookies</strong>
            <p>Ce site utilise des cookies pour améliorer votre expérience de navigation. Vous pouvez accepter tous les cookies, refuser les cookies optionnels, ou personnaliser vos préférences.</p>
          </div>
          <div className="cookie-consent-actions">
            <Button
              variant="primary"
              onClick={handleAccept}
              className="choice-button"
            >
              Accepter
            </Button>
            <Button
              variant="outline-secondary"
              onClick={handleRefuse}
              className="choice-button"
            >
              Refuser
            </Button>
            <Button
              variant="outline-primary"
              onClick={() => setShowPreferences(true)}
              className="choice-button"
            >
              Gérer les cookies
            </Button>
          </div>
        </div>
      </div>

      <Modal show={showPreferences} onHide={() => setShowPreferences(false)} centered className="cookie-preferences-modal login-modal">
        <Modal.Header closeButton className="custom-modal-header">
          <Modal.Title className="custom-modal-title">
            <i className="bi bi-shield-lock-fill me-2"></i>
            Gérer les cookies
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Vous pouvez activer ou désactiver les différentes catégories de cookies selon vos préférences. Les cookies nécessaires restent toujours actifs pour assurer le bon fonctionnement du site.</p>
          <Form>
            {Object.entries(consent).map(([key, value]) => {
              const meta = categoryMeta[key as keyof typeof defaultConsent];
              return (
                <div key={key} className="cookie-preference-item mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div>
                      <strong>{meta.label}</strong>
                    </div>
                    <Form.Check
                      type="switch"
                      id={`cookie-consent-${key}`}
                      checked={value}
                      disabled={key === 'necessary'}
                      onChange={() => handleToggleCategory(key as keyof typeof defaultConsent)}
                    />
                  </div>
                  <div className="cookie-preference-description text-muted">{meta.description}</div>
                </div>
              );
            })}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" className="choice-button" onClick={() => setShowPreferences(false)}>
            Annuler
          </Button>
          <Button variant="primary" className="choice-button" onClick={handleSavePreferences}>
            Enregistrer
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CookieConsent;

